import {
  Divider,
  FormControl,
  FormHelperText,
  InputLabel,
  ListSubheader,
  MenuItem,
  SxProps
} from '@mui/material'
import { styled, Theme } from '@mui/material/styles'
import { uniqueId } from 'lodash'
import React, { useEffect, useState } from 'react'

import { DownArrow } from '../../../assets/icons'
import CustomSelect, { CustomSelectProps } from './CustomSelect'
import MultiSelectListBox from './MultiSelectListBox'

export interface Item {
  value: any
  label: string
  isSeperator?: boolean
  isArchived?: boolean
  active?: boolean
}

interface SelectProps extends Omit<CustomSelectProps, 'component'> {
  italic?: boolean
  items: Item[]
  label?: string
  helperText?: string
  withNullField?: boolean
  renderUnknownField?: boolean
  showOnlyField?: { value: any; label?: string }
  onChange?: (value?: any) => void
  onClick?: (value?: any) => void
  sx?: SxProps<Theme>
}

interface StyledFormControlProps extends React.ComponentProps<typeof FormControl> {
  italic?: boolean
}

const StyledFormControl: React.FC<StyledFormControlProps> = styled(FormControl)(
  ({ italic }: StyledFormControlProps) =>
    italic
      ? { '& > .MuiInputLabel-outlined': { fontStyle: 'italic' } }
      : { '& label + .MuiInput-formControl': { marginTop: '12px' } }
)
const Select: React.ForwardRefExoticComponent<
  Omit<SelectProps, 'ref'> & React.RefAttributes<unknown>
> = React.forwardRef((props, ref) => {
  const {
    children,
    disabled,
    italic,
    items,
    label,
    minWidth = 110,
    maxWidth = undefined,
    multiple,
    onChange,
    onClick,
    value: propValue,
    variant,
    helperText,
    native,
    showOnlyField,
    error,
    withNullField,
    id,
    renderUnknownField,
    sx
  } = props
  const [value, setValue] = useState<unknown>(multiple ? [] : '')
  const selectItems = withNullField && items ? [{ value: '', label: 'None' }, ...items] : items

  const renderValue = (selected: any) => {
    const label =
      selectItems?.find(item => item?.value === selected)?.label || (renderUnknownField && selected)
    if (label) return label

    // When showOnlyField is set and no actual value is selected, show the showOnlyField value
    if (showOnlyField?.value && !selected) {
      return showOnlyField?.label || showOnlyField?.value
    }
    if (showOnlyField?.value === selected) {
      return showOnlyField?.label || showOnlyField?.value
    }
  }

  const [open, setOpen] = useState(false)
  useEffect(() => {
    if (propValue !== value) setValue(propValue)
  }, [propValue, setValue, value])

  // Only force shrink when showOnlyField has a value and no actual value is selected
  // This is specifically for the hierarchical Result dropdown scenario
  const shouldShrink = showOnlyField?.value && !value ? true : undefined

  // Enable displayEmpty when showOnlyField is set so renderValue is called even with empty value
  const shouldDisplayEmpty = !!showOnlyField?.value

  return (
    <StyledFormControl
      italic={italic}
      variant={variant}
      style={{ minWidth, maxWidth }}
      size="small"
      error={!!error}
      ref={ref as unknown as React.RefObject<HTMLDivElement>}
    >
      <InputLabel disableAnimation htmlFor="select-outlined" shrink={shouldShrink}>
        {label}
      </InputLabel>
      {native && multiple ? (
        <MultiSelectListBox
          items={(selectItems || []).filter(
            item =>
              !!item && (!item.isArchived || (Array.isArray(value) && value.includes(item.value)))
          )}
          value={Array.isArray(value) ? value : []}
          onChange={newValue => onChange?.(newValue)}
          minWidth={minWidth}
          maxWidth={maxWidth}
          disabled={disabled}
        />
      ) : (
        <CustomSelect
          id={id || 'select-outlined'}
          maxWidth={maxWidth}
          minWidth={minWidth}
          multiple={multiple}
          value={value}
          displayEmpty={shouldDisplayEmpty}
          renderValue={showOnlyField || renderUnknownField ? renderValue : undefined}
          onChange={({ target: { value: newValue } }) => onChange?.(newValue)}
          onOpen={() => {
            onClick?.()
            setOpen(true)
          }}
          onClose={() => setOpen(false)}
          open={open}
          label={label}
          disabled={disabled}
          IconComponent={iconProps => <DownArrow {...iconProps} style={{ top: 'inherit' }} />}
          native={native}
          sx={sx}
        >
          {selectItems
            ? [native && { label: '', value: undefined }, ...selectItems]
                .filter(item => !!item && (!item.isArchived || item.value === value))
                .map((item, index) => {
                  if (native && item) {
                    return (
                      <option
                        key={item.value}
                        value={item.value}
                        disabled={index === 0}
                        hidden={index === 0}
                        selected={(!value && index === 0) || value === item.value}
                        title={item.label}
                      >
                        {item.label}
                      </option>
                    )
                  }
                  if ((item as Item)?.isSeperator && index !== 0) {
                    return <ListSubheader component={Divider} key={uniqueId()} />
                  }
                  return (
                    <MenuItem
                      component={native ? 'option' : 'li'}
                      key={item ? item?.value : uniqueId()}
                      value={item ? item?.value : undefined}
                      sx={{
                        ...(item && item?.value === ''
                          ? {
                              '&.MuiMenuItem-root': {
                                fontStyle: 'italic'
                              }
                            }
                          : {}),
                        '&:hover': {
                          backgroundColor: '#6b7280',
                          color: 'white'
                        },
                        '&.Mui-selected': {
                          backgroundColor: '#9ca3af',
                          color: 'white'
                        },
                        '&.Mui-selected:hover': {
                          backgroundColor: '#4b5563'
                        }
                      }}
                    >
                      {item ? item?.label : ''}
                    </MenuItem>
                  )
                })
            : children}
        </CustomSelect>
      )}
      <FormHelperText>{helperText}</FormHelperText>
    </StyledFormControl>
  )
})

export default Select
